import type { ReactiveFormValue, FormField, DropdownData } from '@fitogest/reactive-form-builder/interfaces';
import type { ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import type { FullMapModel } from 'src/app/models/form-common/full-map.model';
import type { ReferenciaCatastral } from './dialog/dialog.sigpac.component';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import {
    ParcelasDropdownContextFactory,
    type ParcelasFormBuilderRefLike,
} from './parcelas-dropdown-context.factory';
import type { ParcelasDropdownDataSourceFactory } from './parcelas-dropdown-datasource.factory';
import {
    ParcelasMapAdapter,
    type ParcelasIncomingMapContext,
    type ParcelasIncomingMapData,
    type ParcelasIncomingMapResult,
    type ParcelasMapCatalogContext,
} from './parcelas-map.adapter';
import {
    ParcelasMapSubmitAdapter,
    type ParcelasMapSubmitContext,
    type ParcelasMapSubmitResult,
} from './parcelas-map-submit.adapter';
import {
    ParcelasReaFincaSelectionAdapter,
    type ParcelasReaFincaSelectionSyncContext,
    type ParcelasReaFincaSelectionSyncResult,
} from './parcelas-rea-finca-selection.adapter';
import { ParcelasFormRequestLifecycleAdapter } from './parcelas-form-request-lifecycle.adapter';
import {
    ParcelasNewOptionAdapter,
    type ParcelasNewComunidadRegantesContext,
} from './parcelas-new-option.adapter';
import type { ParcelasVariedadVariosCreateRequest } from './parcelas-variedad-varios.adapter';

export type ParcelasFormFacadeContext = {
    readonly dataSourceFactory: ParcelasDropdownDataSourceFactory;
    readonly getFormBuilderRef: () => ParcelasFormBuilderRefLike | null | undefined;
    readonly getFormModel: () => Partial<ParcelasModel> | null | undefined;
    readonly getFallbackModel: () => Partial<ParcelasModel> | null | undefined;
    readonly getLegacyCultivoSelected: () => unknown;
    readonly getLegacyVariedadSelected: () => unknown;
    readonly getFormType: () => number;
    readonly getUserId: () => unknown;
    readonly getClientId: () => unknown;
    readonly refreshReactiveDropdownMappings: () => void;
};

export type ParcelasIncomingMapFacadeContext = Omit<ParcelasIncomingMapContext, 'catalogContext'>;

export type ParcelasMapSubmitFacadeContext = Omit<ParcelasMapSubmitContext, 'catalogContext'>;


export type ParcelasIncomingMapApplyFacadeContext = {
    readonly rawData: string;
    readonly currentMap: FullMapModel | null | undefined;
    readonly currentMaps: readonly FullMapModel[] | null | undefined;
    readonly env: ParcelasEnvironmentDefinition;
    readonly userId: string;
    readonly shouldUpdateName: boolean;
    readonly applyModelPatch: (patch: Partial<ParcelasModel>) => void;
    readonly setMaps: (maps: readonly FullMapModel[]) => void;
    readonly setSuggestedName: (name: string) => void;
    readonly closeMap: () => void;
    readonly updateReactiveForm: () => void;
    readonly markNombrePristine: () => void;
};


export type ParcelasSigpacReferenceFacadeContext = {
    readonly event: object;
    readonly applyModelPatch: (patch: Partial<ParcelasModel>) => void;
    readonly updateReactiveForm: () => void;
};

export type ParcelasNewComunidadRegantesFacadeContext = Omit<
    ParcelasNewComunidadRegantesContext,
    'formBuilderRef' | 'formType' | 'userId' | 'clientId' | 'refreshReactiveDropdownMappings'
>;

export type ParcelasFormRequestLifecycleFacadeContext = {
    readonly formRequest: ReactiveFormRequestCoordinator<ParcelasModel>;
    readonly env: ParcelasEnvironmentDefinition;
    readonly formFieldsDatosParcela: readonly FormField<object>[];
    readonly variedadesDropdown: DropdownData<VisitasVariedadMesModel>;
    readonly createVariedad: (
        request: ParcelasVariedadVariosCreateRequest
    ) => Promise<readonly Partial<VisitasVariedadMesModel>[] | null | undefined>;
    readonly applyLegacyModelPatch: (patch: Partial<ParcelasModel>) => void;
    readonly markVariedadVariosCreated: () => void;
    readonly applyFormModelPatch: (patch: Partial<ReactiveFormValue<ParcelasModel>>) => void;
    readonly logError?: (message: string, error: unknown) => void;
};

export type ParcelasAfterSendFacadeContext = {
    readonly backendResponse: unknown;
    readonly currentMap: FullMapModel | null | undefined;
    readonly env: ParcelasEnvironmentDefinition;
    readonly userId: string;
    readonly customMapColors: Record<string, string>;
    readonly variedadVariosCreated: boolean;
    readonly loadExistingMaps: () => Promise<readonly FullMapModel[] | null | undefined>;
    readonly updateMap: (map: FullMapModel) => Promise<unknown>;
    readonly createMap: (map: FullMapModel) => Promise<unknown>;
    readonly setAuxMap: (map: FullMapModel) => void;
    readonly reloadPage: () => void;
    readonly goBack: () => void;
    readonly logError?: (message: string, error: unknown) => void;
};

export type ParcelasAfterSendFacadeResult = {
    readonly syncResult: ParcelasMapSubmitResult | null;
    readonly finalMap: FullMapModel | null;
};

export type ParcelasReloadComponentFacadeContext = {
    readonly closeMap: () => void;
    readonly reloadPage: () => void;
};


/**
 * Coordinates Parcelas-specific adapters from a single entrypoint.
 *
 * The Angular component owns view state and injected services, while this facade centralizes shared dropdown/catalog
 * context creation and delegates to pure adapters for map, REA and request-lifecycle behaviour.
 */
export class ParcelasFormFacade {

    public constructor(private readonly context: ParcelasFormFacadeContext) {}

    public createDropdownContextFactory(): ParcelasDropdownContextFactory {
        return new ParcelasDropdownContextFactory({
            dataSourceFactory: this.context.dataSourceFactory,
            formBuilderRef: this.context.getFormBuilderRef(),
            formModel: this.context.getFormModel(),
            fallbackModel: this.context.getFallbackModel(),
            legacyCultivoSelected: this.context.getLegacyCultivoSelected(),
            legacyVariedadSelected: this.context.getLegacyVariedadSelected(),
        });
    }

    public createMapCatalogContext(): ParcelasMapCatalogContext {
        return this.createDropdownContextFactory().createMapCatalogContext();
    }

    public withMapColor(map: FullMapModel, customMapColors: Record<string, string | undefined>): FullMapModel {
        return ParcelasMapAdapter.withColor(map, this.createMapCatalogContext(), customMapColors);
    }

    public buildIncomingMapData(context: ParcelasIncomingMapFacadeContext): ParcelasIncomingMapResult {
        return ParcelasMapAdapter.buildIncomingMapData({
            ...context,
            catalogContext: this.createMapCatalogContext(),
        });
    }


    public applyIncomingMapData(context: ParcelasIncomingMapApplyFacadeContext): ParcelasIncomingMapResult {
        const data = JSON.parse(context.rawData) as ParcelasIncomingMapData;
        const result = this.buildIncomingMapData({
            model: this.context.getFormModel() ?? {},
            currentMap: context.currentMap,
            data,
            env: context.env,
            userId: context.userId,
            shouldUpdateName: context.shouldUpdateName,
        });

        context.applyModelPatch(result.modelPatch);
        context.setMaps([
            result.map,
            ...(context.currentMaps ?? []).slice(1),
        ]);

        if (result.suggestedName !== '') {
            context.setSuggestedName(result.suggestedName);
        }

        context.closeMap();
        context.updateReactiveForm();
        context.markNombrePristine();

        return result;
    }

    public applySigpacReference(context: ParcelasSigpacReferenceFacadeContext): Partial<ParcelasModel> {
        const reference = context.event as ReferenciaCatastral;
        const patch: Partial<ParcelasModel> = {
            cod_provincia: reference.provincia ?? '',
            municipio: reference.municipio ?? '',
            poligono: reference.poligono ?? '',
            numero: reference.parcela ?? '',
        };

        context.applyModelPatch(patch);
        context.updateReactiveForm();

        return patch;
    }

    public synchronizeSubmittedMap(context: ParcelasMapSubmitFacadeContext): Promise<ParcelasMapSubmitResult> {
        return ParcelasMapSubmitAdapter.synchronizeSubmittedMap({
            ...context,
            catalogContext: this.createMapCatalogContext(),
        });
    }

    public synchronizeReaFincaSelection(
        context: ParcelasReaFincaSelectionSyncContext,
    ): ParcelasReaFincaSelectionSyncResult {
        return ParcelasReaFincaSelectionAdapter.synchronize(context);
    }

    public async afterSend(context: ParcelasAfterSendFacadeContext): Promise<ParcelasAfterSendFacadeResult> {
        let syncResult: ParcelasMapSubmitResult | null = null;
        let finalMap: FullMapModel | null = null;

        if (context.currentMap) {
            const coloredMap = this.withMapColor(context.currentMap, context.customMapColors);

            context.setAuxMap(coloredMap);

            const baseSubmitContext = {
                backendResponse: context.backendResponse,
                currentMap: coloredMap,
                env: context.env,
                userId: context.userId,
                loadExistingMaps: context.loadExistingMaps,
                updateMap: context.updateMap,
                createMap: context.createMap,
            } satisfies Omit<ParcelasMapSubmitFacadeContext, 'logError'>;
            const submitContext = context.logError
                ? {
                    ...baseSubmitContext,
                    logError: context.logError,
                }
                : baseSubmitContext;

            syncResult = await this.synchronizeSubmittedMap(submitContext);

            if (syncResult.map !== null) {
                finalMap = syncResult.map;
                context.setAuxMap(syncResult.map);
            }
        }

        if (context.variedadVariosCreated) {
            context.reloadPage();
        }

        context.goBack();

        return {
            syncResult,
            finalMap,
        };
    }

    public reloadComponent(context: ParcelasReloadComponentFacadeContext): void {
        context.closeMap();
        context.reloadPage();
    }

    public saveComunidadRegantes(context: ParcelasNewComunidadRegantesFacadeContext): void {
        ParcelasNewOptionAdapter.saveComunidadRegantes({
            ...context,
            formBuilderRef: this.context.getFormBuilderRef() ?? null,
            formType: this.context.getFormType(),
            userId: this.context.getUserId(),
            clientId: this.context.getClientId(),
            refreshReactiveDropdownMappings: this.context.refreshReactiveDropdownMappings,
        });
    }

    public registerSendBefore(context: ParcelasFormRequestLifecycleFacadeContext): void {
        ParcelasFormRequestLifecycleAdapter.registerSendBefore({
            ...context,
            createDropdownContextFactory: () => this.createDropdownContextFactory(),
        });
    }
}
